import axios from "axios";
import {
  getItemStorage,
  setItemToStorage,
  clearItemStorage,
} from "../utils/utils";
import { supabase } from "../utils/supabaseClient";
import { refreshAuthToken } from "../utils/refreshAuthToken";
import { BASE_API_URL } from "./api";

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, data = null) => {
  if (error) {
    // Reject all queued requests so they don't hang forever
    failedQueue.forEach(({ reject }) => reject(error));
    failedQueue = [];
    // DY: FOR LOGOUT AND CLEAR TOKEN
    clearItemStorage();
    // Skip the redirect if we're already on /login — otherwise an unauthenticated
    // 401 (e.g. a global query that fires on the login page itself) triggers a
    // hard navigation back to /login, which remounts the app, refires the query,
    // and produces a visible refresh loop. Use endsWith() to handle the
    // BrowserRouter basename ("/dashboard/login").
    if (!window.location.pathname.endsWith("/login")) {
      window.location.href = "/login";
    }
  } else {
    // Update userDetails with full token shape (including expireDate)
    const existing = getItemStorage("userDetails") || {};
    const expiresIn = parseInt(data.expiresIn, 10) || 3600;
    const updated = {
      ...existing,
      id_token: data.id_token,
      refresh_token: data.refresh_token,
      expiresIn: expiresIn,
      expireDate: new Date(Date.now() + expiresIn * 1000).toISOString(),
    };
    setItemToStorage("userDetails", updated);
    // Mirror new tokens into supabase-js storage so direct supabase.* calls
    // and getSupabaseRefreshToken() see the latest credentials. setSession
    // does NOT trigger another refresh — it just rewrites the SDK's storage.
    // Skip on the SDK path: refreshSession() already persisted, and a redundant
    // setSession() fires an extra /auth/v1/user round-trip and a spurious
    // SIGNED_IN event.
    if (data.id_token && data.refresh_token && !data.__fromSupabaseSdk) {
      supabase.auth
        .setSession({
          access_token: data.id_token,
          refresh_token: data.refresh_token,
        })
        .catch((e) => console.error("[AUTH] supabase setSession failed:", e));
    }
    // Resolve all queued requests with the new token
    console.log("[AUTH] processQueue: resolving", failedQueue.length, "queued requests");
    failedQueue.forEach(({ resolve }) => resolve(data.id_token));
    failedQueue = [];
  }
};

const instance = axios.create({
  baseURL: BASE_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// INTERCEPTOR REQUEST
instance.interceptors.request.use(
  async (config) => {
    const tokenDetails = getItemStorage("userDetails") || null;
    if (tokenDetails) {
      config.headers["Authorization"] = "Bearer " + tokenDetails.id_token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    const tokenDetails = getItemStorage("userDetails") || null;
    const originalRequest = error.config;

    if (error.response) {
      if (error.response.status === 401 && !originalRequest._retry) {
        console.log("[AUTH 401] url:", originalRequest.url, "isRefreshing:", isRefreshing);
        if (isRefreshing) {
          return new Promise(function (resolve, reject) {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers["Authorization"] = "Bearer " + token;
              return instance(originalRequest);
            })
            .catch((err) => {
              // Reject so queued callers hit onError, matching the rest of
              // this interceptor (PR #217). Returning the error here would
              // silently resolve the promise.
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        return new Promise(function (resolve, reject) {
          refreshAuthToken(tokenDetails)
            .then((data) => {
              console.log("[AUTH 401] refresh SUCCESS via", data.__fromSupabaseSdk ? "supabase-js" : "spring-boot", "new token (first 20):", data.id_token?.substring(0, 20));
              instance.defaults.headers.common["Authorization"] =
                "Bearer " + data.id_token;
              originalRequest.headers["Authorization"] =
                "Bearer " + data.id_token;
              processQueue(null, data);
              resolve(instance(originalRequest));
            })
            .catch((err) => {
              console.error("[AUTH 401] refresh FAILED:", err?.response?.status, err?.response?.data || err.message);
              processQueue(err, null);
              reject(err);
            })
            .then(() => {
              isRefreshing = false;
            });
        });
      }

      // HTTP 4xx/5xx: propagate as rejection so react-query (and other axios
      // callers) hit their onError / .catch handlers. Previously this returned
      // `error.response`, which resolved the promise as if the request had
      // succeeded — masking server errors and producing silent data-loss bugs
      // (see PR #217: Dr. Wigati assessment save failure).
      return Promise.reject(error);
    }

    // Network error / timeout / CORS / DNS / aborted request: no `error.response`.
    // Must reject explicitly — falling off the end of the handler would resolve
    // the promise with `undefined` and cascade into the same silent failure.
    return Promise.reject(error);
  }
);

export default instance;
